import React from "react";
import { connect } from "react-redux";
import { MenuItem } from "react-bootstrap";
import RootCloseWrapper from "react-overlays/lib/RootCloseWrapper";
import PROCESS_TYPES from "@common/constants/processTypes";
import * as sessionService from "@brewery/services/sessionService";
import { isSessionStuck } from "@brewery/util/sessionStuck";
import {
  isDeviceKeg,
  isDeviceIdleProcess,
  isDeviceOffline,
  OFFLINE_COMMAND_REASON,
} from "@util/deviceStatusHelper";

export class DeviceMenu extends React.Component {
  handleRootClose = (event) => {
    this.props.onClose(event, { source: "rootClose" });
  };

  showCleaningModal = () => {
    const { showCleaningModal, device } = this.props;
    showCleaningModal(device.uuid);
  };

  showUnregisterDeviceModal = () => {
    const { showUnregisterDeviceModal, device } = this.props;
    showUnregisterDeviceModal(device.uuid);
  };

  pauseSession = () => {
    const { device } = this.props;
    sessionService.updateSession(device.session_id, { command_type: 2 });
  };

  render() {
    const {
      showDeviceModal,
      device,
      showWifiModal,
      isDisconnected,
      showStopSessionModal,
      showResetSessionModal,
      showEditFermentationModal,
      brewEligible = true,
      nextBrewDate,
    } = this.props;
    const isStarting = device.process_type == 0 && device.session_id;
    const disableStopSession = device.session_id === null || !!isStarting;
    const isKeg = isDeviceKeg(device);
    const isIdle = isDeviceIdleProcess(device);
    const hasSession = device.session_id != null;
    const canReset = isSessionStuck(device);
    // Offline devices cannot receive commands -- the backend publishes the
    // frame and it is dropped. NOT `isDisconnected` (which the props already
    // carry): that flag means offline AND IDLE, so it is false for exactly the
    // fermenting keg this needs to block. See src/util/deviceStatusHelper.js.
    const isOffline = isDeviceOffline(device);
    const isFermentingOrServing =
      device.process_type === PROCESS_TYPES.PROC_FERMENTATION ||
      device.process_type === PROCESS_TYPES.PROC_SERVING;
    // Custom fermentation is available to every subscription tier — there is no
    // tier gate here. The conditions below are purely about the device.
    //
    // "Edit fermentation" is offered for any busy keg (fermenting, serving, or a
    // divergent idle-with-session state) EXCEPT one mid-clean — the swap path
    // STOPs the current session and starts a fresh fermentation, which also
    // recovers a keg whose session diverged from the device state. Cleaning is
    // excluded: replacing a clean cycle with a fermentation makes no sense.
    const isCleaningKeg =
      isKeg && device.process_type === PROCESS_TYPES.PROC_CLEAN_KEG;
    const isEditableKeg = isKeg && hasSession && !isCleaningKeg;
    const isIdleKeg = isKeg && !hasSession && isIdle;

    // Starting a fermentation on an idle keg is a NEW batch, so the backend
    // charges it against the STARTER one-process-per-8-weeks quota and will 400
    // if the caller is inside the cooldown. Disable the entry (never hide it) so
    // the UI blocks exactly what the backend rejects.
    //
    // "Edit fermentation" is deliberately NOT disabled: it supersedes the keg's
    // existing session — the same physical batch — which the backend exempts from
    // the quota. Disabling it would leave a STARTER unable to edit a fermentation
    // they had just started.
    //
    // brewEligible defaults to true so an unresolved or failed eligibility fetch
    // fails open; the backend remains the source of truth.
    const fermentationBlocked = brewEligible === false;
    const cooldownTitle = nextBrewDate
      ? `Explorer plan: one brew or fermentation every 8 weeks. Next available on ${new Date(
          nextBrewDate,
        ).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })}.`
      : "Explorer plan: one brew or fermentation every 8 weeks.";

    return (
      <RootCloseWrapper
        event="click"
        disabled={!this.props.open}
        onRootClose={this.handleRootClose}
      >
        <div className="dropdown-menu header__account__menu">
          <MenuItem
            eventKey="1"
            onSelect={() =>
              showDeviceModal(device.uuid || device.serial_number)
            }
          >
            Rename
          </MenuItem>
          <MenuItem eventKey="2" onClick={showWifiModal}>
            Set up Wi-Fi
          </MenuItem>
          {isFermentingOrServing && (
            // Pause is CMD_PAUSE_PROCESS -- a pure device command with no
            // backend-side fallback. Unlike Stop and Reset, which end the
            // session backend-side and must stay reachable for a device that
            // never reconnects, Pause is worthless (and unactionable) offline.
            <MenuItem
              eventKey="3"
              onSelect={this.pauseSession}
              disabled={isOffline || !device.session_id}
              title={isOffline ? OFFLINE_COMMAND_REASON : undefined}
            >
              <span style={{ color: "#1565c0" }}>Pause</span>
            </MenuItem>
          )}
          {!disableStopSession && (
            <MenuItem
              eventKey="4"
              onSelect={() => showStopSessionModal(device.session_id)}
            >
              <span style={{ color: "#e53935" }}>Stop</span>
            </MenuItem>
          )}
          {/* Available for every device type, online or offline: stuck
              sessions (hard pending_command_error) have been observed on
              brewers as well as kegs, and offline devices can need their
              session ended backend-side too. */}
          {hasSession && (
            <MenuItem
              eventKey="3a"
              onSelect={() => showResetSessionModal(device.session_id)}
              disabled={!canReset}
              title={
                canReset
                  ? "Recover this stuck session (PAUSE + END)"
                  : "Available only when the session is stuck"
              }
            >
              Reset
            </MenuItem>
          )}
          {isEditableKeg && (
            <MenuItem
              eventKey="3b"
              onSelect={() => showEditFermentationModal(device)}
              disabled={isOffline}
              title={isOffline ? OFFLINE_COMMAND_REASON : undefined}
            >
              Edit fermentation
            </MenuItem>
          )}
          {isIdleKeg && (
            <MenuItem
              eventKey="3c"
              onSelect={() => showEditFermentationModal(device)}
              disabled={isOffline || fermentationBlocked}
              title={
                isOffline
                  ? OFFLINE_COMMAND_REASON
                  : fermentationBlocked
                    ? cooldownTitle
                    : undefined
              }
            >
              Start fermentation
            </MenuItem>
          )}
          {!isKeg && (
            <React.Fragment>
              <MenuItem
                eventKey="5"
                onSelect={this.showCleaningModal}
                disabled={isDisconnected || isKeg || !isIdle}
              >
                Clean
              </MenuItem>
              <MenuItem eventKey="6" onSelect={this.showUnregisterDeviceModal}>
                Unregister
              </MenuItem>
            </React.Fragment>
          )}
          <MenuItem disabled={true}>
            <p className="firmware-label">
              Firmware: <br />
              {device.software_version}
            </p>
          </MenuItem>
        </div>
      </RootCloseWrapper>
    );
  }
}

function mapStateToProps({ userInfo }) {
  return { userInfo };
}

export default connect(mapStateToProps, undefined, undefined, {
  forwardRef: true,
})(DeviceMenu);
